import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Field, FormError } from '@/components/form';
import { AuthShell } from '@/features/auth/auth-shell';
import { NewPasswordFields, passwordIssue } from '@/features/auth/new-password-fields';
import { useHashToken } from '@/features/auth/use-hash-token';
import { api, errorMessage } from '@/lib/api';

export const Route = createFileRoute('/invite')({ component: InvitePage });

interface InviteInfo {
  email: string;
  name: string;
  clientName: string | null;
}

function InvitePage() {
  const token = useHashToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.post<InviteInfo>('/v1/auth/invites/lookup', { token }),
    enabled: !!token,
    retry: false,
  });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (info.data) setName(info.data.name);
  }, [info.data]);

  const accept = useMutation({
    mutationFn: () => api.post('/v1/auth/invites/accept', { token, name, password }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['me'] });
      await navigate({ to: '/setup-2fa', search: {} });
    },
  });

  const signIn = (
    <Link to="/login" search={{}} className="text-link underline-offset-4 hover:underline">
      Already set up? Sign in
    </Link>
  );

  if (!token || info.isError) {
    return (
      <AuthShell
        title="Invitation not valid"
        description="This invitation link has expired, has already been used, or is incomplete. Ask your administrator to send a new one."
        footer={signIn}
      >
        <span />
      </AuthShell>
    );
  }

  const issue = passwordIssue(password, confirm);
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!issue && name.trim()) accept.mutate();
  }

  return (
    <AuthShell
      title="Create your account"
      description={
        info.data ? (
          <>
            You’ve been invited to Fraud Safe{info.data.clientName ? <> for <strong className="text-foreground">{info.data.clientName}</strong></> : null} as{' '}
            <strong className="text-foreground">{info.data.email}</strong>.
          </>
        ) : (
          <Skeleton className="h-4 w-64" />
        )
      }
      footer={signIn}
    >
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <FormError message={accept.isError ? errorMessage(accept.error) : null} />
        <Field id="name" label="Your name">
          <Input id="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <NewPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
        <Button type="submit" size="lg" disabled={!info.data || !!issue || !name.trim() || accept.isPending}>
          {accept.isPending ? 'Creating account…' : 'Continue to two-step setup'}
        </Button>
      </form>
    </AuthShell>
  );
}
